/**
 * Service Provider Login Page
 * Magic link authentication for waste management service providers
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { serviceProviderService } from '../services/service-provider.service';

export function ServiceProviderLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [usePassword, setUsePassword] = useState(true); // Default to password login
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const validateToken = useCallback(async (token: string) => {
    try {
      await serviceProviderService.validateMagicLink(token);
      navigate('/provider/dashboard');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Invalid or expired magic link'
      );
    }
  }, [navigate]);

  // Handle magic link validation on page load
  useEffect(() => {
    const token = searchParams.get('token');

    if (token) {
      validateToken(token);
    } else if (serviceProviderService.isAuthenticated()) {
      // Already authenticated, redirect to dashboard
      navigate('/provider/dashboard');
    }
  }, [searchParams, navigate, validateToken]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (usePassword && !password.trim()) {
      setError('Please enter your password');
      return;
    }

    setIsSubmitting(true);

    try {
      if (usePassword) {
        // Email/password login
        await serviceProviderService.login(email, password);
        navigate('/provider/dashboard');
      } else {
        // Magic link login
        await serviceProviderService.requestMagicLink(email);
        setSuccessMessage(
          'Check your email! We sent you a magic link to sign in.'
        );
        setEmail('');
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : usePassword
            ? 'Failed to log in. Please check your credentials.'
            : 'Failed to send magic link. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-8 h-8 text-accent-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>
        <h1 className="text-3xl font-bold text-neutral-900 mb-2">
          Service Provider Login
        </h1>
        <p className="text-neutral-600">
          Access your dashboard to view available jobs and manage bids
        </p>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 p-4 bg-accent-50 border border-accent-200 rounded-lg flex items-start">
          <svg
            className="w-5 h-5 text-accent-600 mr-3 mt-0.5 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <p className="text-accent-900 font-medium">{successMessage}</p>
            <p className="text-accent-700 text-sm mt-1">
              Click the link in your email to access your dashboard
            </p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
          <svg
            className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-red-900">{error}</p>
        </div>
      )}

      {/* Login Form */}
      <div className="card">
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-field"
              placeholder="provider@example.com"
              disabled={isSubmitting}
              required
            />
          </div>

          {usePassword && (
            <div className="mb-6">
              <label htmlFor="password" className="block text-sm font-medium text-neutral-700 mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field"
                placeholder="••••••••"
                disabled={isSubmitting}
                required
              />
            </div>
          )}

          {!usePassword && (
            <p className="text-sm text-neutral-500 mb-6">
              We&apos;ll send you a magic link to sign in securely
            </p>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary w-full"
          >
            {isSubmitting ? (
              <>
                <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                {usePassword ? 'Signing in...' : 'Sending Magic Link...'}
              </>
            ) : (
              usePassword ? 'Sign In' : 'Request Magic Link'
            )}
          </button>

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => {
                setUsePassword(!usePassword);
                setPassword('');
                setError(null);
              }}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              {usePassword
                ? 'Use magic link instead'
                : 'Sign in with password'}
            </button>
          </div>
        </form>

        <div className="mt-6 pt-6 border-t border-neutral-200">
          <p className="text-sm text-neutral-600 text-center">
            Are you a contractor?{' '}
            <a href="/" className="text-primary-600 hover:text-primary-700 font-medium">
              Go to main site
            </a>
          </p>
        </div>
      </div>

      {/* Info Box */}
      <div className="mt-8 p-4 bg-neutral-100 rounded-lg">
        <h3 className="font-semibold text-neutral-900 mb-2">
          New to CalWMP Service Providers?
        </h3>
        <p className="text-sm text-neutral-600 mb-3">
          Get access to construction waste management projects across California:
        </p>
        <ul className="text-sm text-neutral-600 space-y-2">
          <li className="flex items-start">
            <svg
              className="w-4 h-4 text-accent-600 mr-2 mt-0.5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            Browse available jobs and submit competitive bids
          </li>
          <li className="flex items-start">
            <svg
              className="w-4 h-4 text-accent-600 mr-2 mt-0.5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            Track your bids and manage awarded projects
          </li>
          <li className="flex items-start">
            <svg
              className="w-4 h-4 text-accent-600 mr-2 mt-0.5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            Connect with contractors across California
          </li>
        </ul>
        <div className="mt-4 pt-4 border-t border-neutral-300">
          <Link
            to="/provider/request-invite"
            className="btn-primary w-full text-center"
          >
            Request Invite Code
          </Link>
          <p className="text-xs text-neutral-500 mt-2 text-center">
            Start with 3 free job bids per month
          </p>
        </div>
      </div>
    </div>
  );
}
